import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Box, Paper, Typography, Switch, alpha, useTheme, Avatar, IconButton, Divider, Button, TextField, Stack, Grid, Collapse, Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LuBuilding, LuUsers, LuPackage, LuSave, LuX, LuTrash2, LuCalendar, LuMail, LuPhone, LuShieldCheck, LuActivity, LuLayoutDashboard, LuTrendingUp, LuPlus, LuRefreshCw } from "react-icons/lu";
import { MdPieChart } from "react-icons/md";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { Company } from "../../db_access/company";
import AgGrid from "../../components/grid/AgGrid";
import { centerAlignColDef, defaultColDef, leftAlignColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import SearchBox from "../../components/input/SearchBox";
import CheckBox from "../../components/input/CheckBox";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import { useContextProvider } from "../../components/page-context/ContextProvider";

// Sub-components from standard layout
import MetricCard from "../../app_components/MetricCard";
import AddButton from "../../components/button/AddButton";
import RefreshButton from "../../components/button/RefreshButton";
import ExportAsXLSXButton from "../../components/export/ExportAsXLSXButton";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import ContextMenu from "../../components/context-menu/ContextMenu";

import { GRAPH_PALETTE } from "../../theme/style/graphChartPalette";
import DonutChart from "../../components/chart/DonutChart";
import HorizontalStackedBarChart from "../../components/chart/HorizontalStackedBarChart";
import AdminGlobalView from "./AdminGlobalView";

dayjs.extend(relativeTime);
const BRAND_GREEN = "#659d33";

const StatusToggleChip = ({ value, onToggle, tok, t }) => {
  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 1.2,
        cursor: "pointer",
        px: 1,
        height: "100%",
        transition: "opacity 0.2s ease",
        "&:hover": { opacity: 0.8 }
      }}
    >
      <Box sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        bgcolor: value ? "#10b981" : "#ef4444"
      }} />
      <Typography sx={{
        fontSize: "13px",
        fontWeight: 500,
        color: value ? "#059669" : "#dc2626",
        fontFamily: tok.font.family
      }}>
        {value ? t("Active") : t("Inactive")}
      </Typography>
    </Box>
  );
};

const SectionHeader = ({ title, tok, color = BRAND_GREEN }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.2,
      color: tok.text.primary,
      fontFamily: tok.font.family,
      fontWeight: tok.font.weight.medium,
      fontSize: '14px',
      borderLeft: `3px solid ${color}`,
      pl: 1.2,
      lineHeight: 1
    }}>
      {title}
    </Typography>
  </Box>
);

const ManageCompanies = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tok = useMemo(() => getOrgTokens(isDark), [isDark]);
  const { enqueueAlert } = useContextProvider();

  const [companies, setCompanies] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Detail state
  const [showDetail, setShowDetail] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);

  // View Toggle: Registry vs Intelligence
  const [activeView, setActiveView] = useState(false);

  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  const gridRef = useRef(null);

  const fetchCompanies = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await Company.get_all();
      if (res.ok) {
        const data = await res.json();
        setCompanies(data);
      }
    } catch (error) { /* handled */ }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies, refresh]);

  const handleNewCompany = async () => {
    try {
      const res = await Company.new();
      if (res.ok) {
        enqueueAlert({ message: t("New organization entry initialized"), severity: "success" });
        setRefresh(prev => !prev);
      }
    } catch (err) { /* handled */ }
  };

  const handleSaveDetails = async () => {
    if (!editingOrg) return;
    try {
      const fullPhone = [editingOrg.phone1, editingOrg.phone2, editingOrg.phone3].filter(Boolean).join("-");
      const dataToSave = {
        ...editingOrg,
        phone: fullPhone,
        auto_renew: editingOrg.auto_renew ? 1 : 0,
        status: editingOrg.status ? 1 : 0
      };

      const res = await Company.update(dataToSave);
      if (res.ok) {
        enqueueAlert({ message: t("Organization parameters synchronized"), severity: "success" });
        setShowDetail(false);
        setEditingOrg(null);
        setRefresh(prev => !prev);
      }
    } catch (err) { /* handled */ }
  };

  const handleDeleteCompanies = async (selected) => {
    if (selected.length === 0) return;
    try {
      const res = await Company.delete(selected.map(s => s.id));
      if (res.ok) {
        enqueueAlert({ message: t("Organization decommissioned"), severity: "success" });
        setSelectedCompanies([]);
        setRefresh(prev => !prev);
        if (editingOrg && selected.some(s => s.id === editingOrg.id)) {
          setShowDetail(false);
          setEditingOrg(null);
        }
      }
    } catch (err) { /* handled */ }
  };

  const filteredData = useMemo(() => {
    if (!searchText) return companies;
    const lower = searchText.toLowerCase();
    return companies.filter(c =>
      c.name?.toLowerCase().includes(lower) ||
      c.primary_contact_name?.toLowerCase().includes(lower) ||
      c.id?.toString().includes(lower)
    );
  }, [companies, searchText]);

  // Analytics Derivation
  const analytics = useMemo(() => {
    const statusData = [
      { label: t("Active Nodes"), value: companies.filter(c => c.status === 1).length, color: "#10b981" },
      { label: t("Inactive Nodes"), value: companies.filter(c => c.status !== 1).length, color: "#ef4444" }
    ];

    const topCapacity = [...companies]
      .sort((a, b) => (Number(b.max_accounts) || 0) - (Number(a.max_accounts) || 0))
      .slice(0, 8)
      .map(c => ({ label: c.name, Capacity: Number(c.max_accounts) || 0 }));

    return { statusData, topCapacity };
  }, [companies, t]);

  const colDefs = [
    {
      headerName: t("Company Name"),
      field: "name",
      flex: 2,
      ...defaultColDef,
      ...leftAlignColDef,
      cellRenderer: (params) => (
        <Typography sx={{ fontWeight: tok.font.weight.medium, color: isDark ? "#ffffff" : "#1e293b", fontFamily: tok.font.family, fontSize: tok.font.size.gridCell }}>
          {params.value}
        </Typography>
      )
    },
    {
      headerName: t("Contact Name"),
      field: "primary_contact_name",
      flex: 1.5,
      ...defaultColDef,
      ...leftAlignColDef,
      cellRenderer: (params) => (
        <Typography sx={{ fontWeight: tok.font.weight.medium, color: isDark ? "#ffffff" : "#1e293b", fontFamily: tok.font.family, fontSize: tok.font.size.gridCell }}>
          {params.value || t("Unassigned")}
        </Typography>
      )
    },
    {
      headerName: t("Status"),
      field: "status",
      width: 160,
      ...centerAlignColDef,
      cellRenderer: (params) => (
        <StatusToggleChip
          value={params.value === 1}
          onToggle={async () => {
            const newVal = params.value === 1 ? 0 : 1;
            try {
              await Company.update({ ...params.data, status: newVal });
              setRefresh(r => !r);
              enqueueAlert({ message: t("Status updated successfully"), severity: "success" });
            } catch (e) { /* handled */ }
          }}
          tok={tok}
          t={t}
        />
      )
    }
  ];

  const handleRowClick = (params) => {
    const phoneParts = (params.data.phone || "").split("-");
    const data = {
      ...params.data,
      phone1: phoneParts[0] || "",
      phone2: phoneParts[1] || "",
      phone3: phoneParts[2] || "",
      auto_renew: params.data.auto_renew === 1,
      status: params.data.status === 1
    };

    if (editingOrg && editingOrg.id === data.id && showDetail) {
      setShowDetail(false);
      setEditingOrg(null);
    } else {
      setEditingOrg(data);
      setShowDetail(true);
    }
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    setMenuPosition({ x: e.pageX, y: e.pageY });
    setMenuVisible(true);
  };

  const contextOptions = [
    { label: t("Decommission Organization"), action: handleDeleteCompanies, needSelectedItems: true }
  ];

  const activeCount = companies.filter(c => c.status === 1).length;
  const totalSeats = companies.reduce((sum, c) => sum + (Number(c.max_accounts) || 0), 0);

  const labelStyle = {
    fontFamily: tok.font.family,
    fontWeight: tok.font.weight.medium,
    color: isDark ? "#ffffff" : "#1e293b",
    mb: 0.8,
    display: 'block',
    fontSize: tok.font.size.gridCell,
    textTransform: 'none',
  };

  const inputStyle = {
    '& .MuiInputBase-root': {
      height: 40,
      fontFamily: tok.font.family,
      fontSize: tok.font.size.gridCell,
      fontWeight: tok.font.weight.medium,
      color: isDark ? '#ffffff' : '#1e293b'
    },
    '& .MuiInputBase-input': {
      py: 0
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      bgcolor: 'transparent'
    }
  };

  const toggleBoxStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    p: 1.25,
    px: 1.5,
    borderRadius: '4px',
    border: '1px solid',
    borderColor: tok.border.card,
    height: 40,
    boxSizing: 'border-box'
  };

  const toggleText = {
    fontFamily: tok.font.family,
    fontWeight: tok.font.weight.medium,
    fontSize: tok.font.size.gridCell,
    color: isDark ? "#ffffff" : "#1e293b"
  };

  return (
    <Stack spacing={tok.spacing.pageGap}>
      <Box sx={{
        ...tok.sectionHeading, 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1.5,
        pl: 0,
        position: 'relative',
        pb: tok.sectionHeading.titlePb,
        borderBottom: tok.sectionHeading.borderBottom
      }}>
        <Box sx={{ 
            width: tok.sectionHeading.barWidth, 
            height: '24px', 
            bgcolor: tok.sectionHeading.barColor, 
            borderRadius: '2px' 
        }} />
        <Typography sx={{ 
            fontFamily: tok.sectionHeading.fontFamily,
            fontSize: tok.sectionHeading.fontSize,
            fontWeight: tok.sectionHeading.fontWeight,
            color: tok.sectionHeading.color
        }}>
          {t("Organizations Management")}
        </Typography>
      </Box>

      {/* Persistence Registry Summary */}
      <Box sx={{ display: 'flex', gap: tok.spacing.metricCardGap, flexWrap: 'wrap', width: '100%' }}>
        {isLoading ? (
          Array.from(new Array(3)).map((_, i) => (
            <Box key={i} sx={{ flex: '1 1 0', minWidth: '240px' }}>
              <Skeleton variant="rounded" height={100} sx={{ borderRadius: tok.radius.card }} />
            </Box>
          ))
        ) : (
          <>
            <Box sx={{ flex: '1 1 0', minWidth: '240px' }}>
              <MetricCard
                title={t("Total Entities")}
                value={companies.length}
                icon={LuBuilding}
                accent={BRAND_GREEN}
                bg={isDark ? alpha(BRAND_GREEN, 0.08) : alpha(BRAND_GREEN, 0.04)}
              />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: '240px' }}>
              <MetricCard
                title={t("Active Portfolio")}
                value={activeCount}
                icon={LuShieldCheck}
                accent="#10b981"
                bg={isDark ? "rgba(16, 185, 129, 0.08)" : "rgba(16, 185, 129, 0.04)"}
              />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: '240px' }}>
              <MetricCard
                title={t("Capacity Licenses")}
                value={totalSeats}
                icon={LuUsers}
                accent="#3b82f6"
                bg={isDark ? "rgba(59, 130, 246, 0.08)" : "rgba(59, 130, 246, 0.04)"}
              />
            </Box>
          </>
        )}
      </Box>

      {/* Main Registry */}
      <Box sx={{
        borderRadius: tok.radius.card,
        bgcolor: tok.bg.card,
        border: '1px solid',
        borderColor: tok.border.card,
        boxShadow: tok.shadow.card,
        overflow: 'hidden'
      }}>
        <TableHeaderContainer>
          <TableTitleContainer>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LuLayoutDashboard size={18} color={BRAND_GREEN} />
              <Typography sx={{ 
                ...tok.tableTitle,
                fontSize: '18px', // High density adjustment
                fontWeight: 600
              }}>
                {t("Organization Entity Ledger")}
              </Typography>
            </Box>
          </TableTitleContainer>
          <TableHeaderToolsContainer>
            <SearchBox text={searchText} setText={setSearchText} placeholder={t("Trace entries...")} width={300} />
            <AddButton onClick={handleNewCompany} />
            <ExportAsXLSXButton data={companies} filename="portfolio_ledger.xlsx" />
            <RefreshButton onClick={() => setRefresh(p => !p)} />
          </TableHeaderToolsContainer>
        </TableHeaderContainer>

        <Box sx={{ p: 0 }}>
          <AgGrid
            ref={gridRef}
            rowData={filteredData}
            columnDefs={colDefs}
            onSelectionChanged={(e) => setSelectedCompanies(e.api.getSelectedRows())}
            onRowClicked={handleRowClick}
            handleContextMenu={handleContextMenu}
            pagination={true}
            rowHeight={52}
            domLayout="autoHeight"
            footerLeftContent={
              <Box sx={{ display: 'flex', alignItems: 'center', ml: -1 }}>
                <CheckBox checked={activeView} onChange={(e) => setActiveView(e.target.checked)} label={t("Global Insights")} />
              </Box>
            }
          />
        </Box>
      </Box>

      {/* Intelligence perspective (Below Grid) */}
      <Collapse in={activeView}>
        <Box>
          <AdminGlobalView
            overrideDonutData={analytics.statusData.map(d => ({
              ...d,
              percent: (d.value / companies.length) * 100
            }))}
            overrideBarData={analytics.topCapacity}
            barKey="Capacity"
            donutTitle={t("Status Topology")}
            barTitle={t("Distribution of Seat Capacity")}
            unitLabel={t("Entities")}
            accentColor={BRAND_GREEN}
            hideFilters={true}
            hideLeaders={true}
          />
        </Box>
      </Collapse>

      {/* Interactive Detail Hub */}
      <Collapse in={showDetail}>
        <Paper sx={{
          width: '100%',
          p: 4,
          pt: 1.5,
          borderRadius: tok.radius.card,
          bgcolor: tok.bg.card,
          border: '1px solid',
          borderColor: tok.border.card,
          boxShadow: tok.shadow.cardHover,
          position: 'relative'
        }}>
          {/* Form Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar variant="rounded" sx={{
                width: 40, height: 40,
                borderRadius: '10px',
                bgcolor: alpha(BRAND_GREEN, 0.08),
                color: BRAND_GREEN,
              }}>
                <LuBuilding size={20} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{
                  fontWeight: tok.font.weight.medium,
                  color: isDark ? "#ffffff" : "#1e293b",
                  fontFamily: tok.font.family,
                  fontSize: tok.font.size.sectionTitle,
                  letterSpacing: '-0.5px'
                }}>
                  {editingOrg?.name || t("Entity Modification Interface")}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setShowDetail(false)} size="small" sx={{ bgcolor: alpha(tok.text.primary, 0.04), border: '1px solid', borderColor: alpha(tok.text.primary, 0.05), '&:hover': { bgcolor: alpha(tok.text.primary, 0.08) } }}>
              <LuX size={18} />
            </IconButton>
          </Box>

          {editingOrg && (
            <Box>
              {/* Row 1: 5 Columns */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)',
                gap: 3,
                mb: 3
              }}>
                <Box>
                  <Typography variant="caption" sx={labelStyle}>{t("Company Name")}</Typography>
                  <TextField fullWidth value={editingOrg.name || ""} onChange={(e) => setEditingOrg({ ...editingOrg, name: e.target.value })} sx={inputStyle} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={labelStyle}>{t("Max Accounts")}</Typography>
                  <TextField fullWidth type="number" value={editingOrg.max_accounts || ""} onChange={(e) => setEditingOrg({ ...editingOrg, max_accounts: e.target.value })} sx={inputStyle} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={labelStyle}>{t("Usage Limit")}</Typography>
                  <TextField fullWidth type="number" value={editingOrg.usage_limit || ""} onChange={(e) => setEditingOrg({ ...editingOrg, usage_limit: e.target.value })} sx={inputStyle} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={labelStyle}>{t("Contact Name")}</Typography>
                  <TextField fullWidth value={editingOrg.primary_contact_name || ""} onChange={(e) => setEditingOrg({ ...editingOrg, primary_contact_name: e.target.value })} sx={inputStyle} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={labelStyle}>{t("Contact Email")}</Typography>
                  <TextField fullWidth value={editingOrg.primary_contact_email || ""} onChange={(e) => setEditingOrg({ ...editingOrg, primary_contact_email: e.target.value })} sx={inputStyle} />
                </Box>
              </Box>

              {/* Row 2: 4 Columns (Different mapping) */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: '1.5fr 1fr 1fr 1fr',
                gap: 3,
                alignItems: 'end'
              }}>
                <Box>
                  <Typography variant="caption" sx={labelStyle}>{t("Phone")}</Typography>
                  <Stack direction="row" spacing={1}>
                    <TextField placeholder="C" value={editingOrg.phone1 || ""} onChange={(e) => setEditingOrg({ ...editingOrg, phone1: e.target.value })} sx={{ width: 60, ...inputStyle }} />
                    <TextField placeholder="E" value={editingOrg.phone2 || ""} onChange={(e) => setEditingOrg({ ...editingOrg, phone2: e.target.value })} sx={{ width: 60, ...inputStyle }} />
                    <TextField placeholder="Number" value={editingOrg.phone3 || ""} onChange={(e) => setEditingOrg({ ...editingOrg, phone3: e.target.value })} sx={{ flex: 1, ...inputStyle }} />
                  </Stack>
                </Box>
                <Box>
                  <Typography variant="caption" sx={labelStyle}>{t("Expiry Date")}</Typography>
                  <TextField fullWidth type="date" value={editingOrg.expiry_date ? dayjs(editingOrg.expiry_date).format("YYYY-MM-DD") : ""} onChange={(e) => setEditingOrg({ ...editingOrg, expiry_date: e.target.value })} sx={inputStyle} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={labelStyle}>{t("Auto Renew")}</Typography>
                  <Box sx={toggleBoxStyle}>
                    <Typography sx={toggleText}>{t("Auto Renew")}</Typography>
                    <Switch size="small" checked={editingOrg.auto_renew} onChange={(e) => setEditingOrg({ ...editingOrg, auto_renew: e.target.checked })} />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" sx={labelStyle}>{t("Status")}</Typography>
                  <Box sx={toggleBoxStyle}>
                    <Typography sx={toggleText}>{t("Status")}</Typography>
                    <Switch size="small" checked={editingOrg.status} onChange={(e) => setEditingOrg({ ...editingOrg, status: e.target.checked })} />
                  </Box>
                </Box>
              </Box>

              {/* Action Toolbar */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, pt: 3, borderTop: `1px solid ${tok.border.card}` }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowDetail(false)}
                  sx={{ px: 4, py: 1, borderRadius: '4px', borderColor: tok.border.card, color: isDark ? "#ffffff" : "#1e293b", fontFamily: tok.font.family, fontWeight: tok.font.weight.medium, fontSize: tok.font.size.gridCell, textTransform: 'none', '&:hover': { bgcolor: alpha(tok.text.primary, 0.05), borderColor: tok.text.primary } }}
                >
                  {t("Discard")}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<LuSave size={18} />}
                  onClick={handleSaveDetails}
                  sx={{
                    px: 5, py: 1,
                    borderRadius: '4px',
                    bgcolor: "#659d33",
                    fontFamily: tok.font.family,
                    fontWeight: tok.font.weight.medium,
                    fontSize: tok.font.size.gridCell,
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: "#4d7a26", boxShadow: 'none' }
                  }}
                >
                  {t("Commit Changes")}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Collapse>
    </Stack>
  );
};

export default ManageCompanies;
